// Pure helpers for the Lab-Visit investigations picker
// (RegistrationInvestigationsSection). Kept out of the component so the dedup
// and line-building rules are unit-testable and single-sourced.
import { LAB_CATEGORY } from '../../constants/procedures';

/**
 * Doctor-advised, not-yet-assigned lab orders from a patient's open-lab-items
 * list — the reception handoff surface. An assigned / in-flight order is
 * mid-workflow and must not be re-offered.
 */
export function advisedAwaitingAssignment(items) {
  return (items || []).filter((o) => o && o.status === 'advised' && !o.assigned_to?.member_id);
}

/**
 * Collapse advised-and-awaiting orders into ONE group per test, so a test
 * advised more than once (repeat advice, or two doctors advising the same test)
 * shows as a single row listing each advisement (doctor + date) instead of
 * duplicate rows. The patient needs the test once, so on assign we keep one
 * order and cancel the rest.
 *
 * Each group:
 *   key                      — stable selection key (procedure_id, else test_name)
 *   procedure_id, test_name
 *   advisements: [{ doctor, date }]   — one per underlying order, oldest first
 *   cost, skip_payment, paid          — paid = ANY underlying order is paid
 *   advised_by_doctor_name            — the primary order's advising doctor
 *   primary_lab_order_id              — order to ASSIGN: prefer an already-paid
 *                                       one (don't waste a payment), else the
 *                                       most recent advice
 *   duplicate_lab_order_ids           — the rest, to CANCEL
 */
export function groupAdvisedByTest(advised) {
  const groups = new Map();
  for (const o of advised || []) {
    if (!o) continue;
    const key = o.catalog_procedure_id || o.test_name;
    if (!key) continue;
    let g = groups.get(key);
    if (!g) {
      g = { key, procedure_id: o.catalog_procedure_id || null, test_name: o.test_name, orders: [] };
      groups.set(key, g);
    }
    g.orders.push(o);
  }

  const ts = (o) => o.origin_suggested_at || o.created_at || '';
  const result = [];
  for (const g of groups.values()) {
    const primary = g.orders.reduce((best, o) => {
      if (!!o.is_paid !== !!best.is_paid) return o.is_paid ? o : best; // a paid order wins
      return ts(o) > ts(best) ? o : best;                              // else the most recent
    }, g.orders[0]);
    const advisements = g.orders
      .map((o) => ({ doctor: o.origin_doctor_name || null, date: (o.origin_suggested_at || '').slice(0, 10) || null }))
      .sort((a, b) => (a.date || '￿').localeCompare(b.date || '￿'));
    result.push({
      key: g.key,
      procedure_id: g.procedure_id,
      test_name: g.test_name,
      advisements,
      cost: Number(primary.final_cost || 0),
      skip_payment: !!primary.skip_payment,
      paid: g.orders.some((o) => !!o.is_paid),
      advised_by_doctor_name: primary.origin_doctor_name || null,
      primary_lab_order_id: primary.lab_order_id,
      duplicate_lab_order_ids: g.orders
        .filter((o) => o.lab_order_id !== primary.lab_order_id)
        .map((o) => o.lab_order_id),
    });
  }
  return result;
}

/**
 * Lab catalog for the "+ Add Investigation" dropdown, EXCLUDING any test a
 * doctor already advised (so a catalog pick can't duplicate an advised test)
 * and anything already picked. Sorted by name.
 *
 * FIX-4 (LAB-ASSIGN-2) — a receptionist/nurse/other caller only sees the
 * `receptionist_allowed` subset (the pool they're authorised to order on
 * their own). A LAB_TECHNICIAN caller gets the FULL active in-house lab
 * catalog, like a doctor advising — owner ruling: the technician is trusted
 * with the whole catalog, not just the front-desk-orderable subset. Dedup
 * against advised/picked ids is unchanged for both.
 */
export function addableLabCatalog(procedures, advisedProcedureIds, pickedIds, isLabTechnicianCaller = false) {
  const advised = advisedProcedureIds instanceof Set ? advisedProcedureIds : new Set(advisedProcedureIds || []);
  const picked = pickedIds instanceof Set ? pickedIds : new Set(pickedIds || []);
  return (procedures || [])
    .filter((p) => p && p.category === LAB_CATEGORY && p.active !== false
      && (isLabTechnicianCaller || p.receptionist_allowed)
      && !advised.has(p.procedure_id) && !picked.has(p.procedure_id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * The source-tagged lines the registration submit consumes:
 *   { source: 'advised', lab_order_id, cancel_lab_order_ids, ... }
 *        → ASSIGN the primary order to the tech, CANCEL the duplicate advisements
 *   { source: 'new', ..., quantity }  → ADVISE a fresh order
 * `groups` come from groupAdvisedByTest; advisedSel is keyed by group `key`.
 * Only ticked advised groups are included; all picked catalog rows are.
 *
 * W6/C5 — a `source:'new'` (registration-picker catalog) line carries
 * `quantity` (int ≥1, default 1 when a row's `quantity` is absent/invalid) —
 * the ONLY qty-bearing line, mirroring W2's procedure quantity. Advised
 * (doctor-suggested) lines NEVER carry `quantity` — they stay qty=1 per
 * owner ruling (registration picker only; advise flow untouched).
 *
 * L1 (lab-credit, IP-linked only) — additive `admissionId`/`ipNumber`: when
 * present (an admission-attach context), EVERY line is stamped
 * `admission_id`/`ip_number` (owner AC5 "stamp at write time as IP", applies
 * to both the advise and assign paths). `creditByProcId` (map/Set of
 * procedure_id → bool, NEW-catalog rows only) carries the per-row credit
 * toggle; a `new` line gets `billing_mode: 'credit'` when the admission
 * context is present, the row isn't opted OUT (`creditByProcId[id] !== false`
 * — IP default is ON per owner ruling D2), AND the item is actually billable
 * (`!skip_payment` — a zero-cost/no-charge test can never be "on credit",
 * nothing to settle). A row explicitly opted OUT (`creditByProcId[id] ===
 * false`, "Collect payment now") gets `billing_mode: 'collect_now'` instead —
 * ONLY under the same admission+billable gate (collect_now is admission-only;
 * this is the ONLY place that flag is minted, so OPD register-visit — which
 * never passes `admissionId` — can never emit it). Doctor-advised
 * (`source:'advised'`) lines NEVER carry `billing_mode` — no money is set by
 * a doctor's advice (AC-NEG); only the IP stamp rides on them. Omitted
 * `admissionId` (plain OPD walk-in) → zero new keys on any line —
 * byte-identical to the pre-L1 shape.
 */
export function buildInvestigationLines({
  groups = [], advisedSel = {}, catalogRows = [], admissionId = null, ipNumber = null, creditByProcId = {},
} = {}) {
  const ipStamp = admissionId ? { admission_id: admissionId, ip_number: ipNumber || null } : {};
  const lines = [];
  for (const g of groups) {
    if (!advisedSel[g.key]) continue;
    lines.push({
      source: 'advised',
      lab_order_id: g.primary_lab_order_id,
      cancel_lab_order_ids: g.duplicate_lab_order_ids || [],
      procedure_id: g.procedure_id,
      name: g.test_name,
      cost: Number(g.cost || 0),
      skip_payment: !!g.skip_payment,
      paid: !!g.paid,
      // The advising doctor — surfaced so the registration token can show
      // "Advised by: Doctor" + the name for a Lab Visit.
      advised_by_doctor_name: g.advised_by_doctor_name || null,
      ...ipStamp,
    });
  }
  for (const p of catalogRows) {
    const qty = Number(p.quantity);
    const billable = admissionId && !p.skip_payment;
    const wantsCredit = billable && creditByProcId[p.procedure_id] !== false;
    const wantsCollectNow = billable && creditByProcId[p.procedure_id] === false;
    lines.push({
      source: 'new',
      procedure_id: p.procedure_id,
      name: p.name,
      cost: Number(p.default_cost || 0),
      skip_payment: !!p.skip_payment,
      paid: false,
      quantity: Number.isInteger(qty) && qty >= 1 ? qty : 1,
      ...ipStamp,
      ...(wantsCredit ? { billing_mode: 'credit' } : {}),
      ...(wantsCollectNow ? { billing_mode: 'collect_now' } : {}),
    });
  }
  return lines;
}
